import { isValueNullish } from "../../../../utils";

/** Original coordinate value and the editable state derived from it. */
export interface CoordinateAxisSource<TSourceValue, TEditableState> {
	value: TSourceValue;
	editableState: TEditableState;
}

/**
 * Determines whether a coordinate part contains user or persisted input.
 *
 * Zero is treated as input; only `undefined`, `null`, and the empty string are absent.
 *
 * @param value - Coordinate part to inspect.
 * @returns Whether the coordinate part contains a value.
 */
export function hasCoordinatePart(value: unknown): boolean {
	return !isValueNullish(value) && value !== "";
}

/**
 * Normalizes a finite numeric coordinate part for editable-state comparisons.
 *
 * @param value - Coordinate part to normalize.
 * @returns Its numeric value when finite, otherwise the original value.
 */
export function getComparableCoordinatePart<T>(value: T): number | T {
	const number = Number(value);
	return hasCoordinatePart(value) && Number.isFinite(number) ? number : value;
}

/**
 * Captures a persisted axis value and the editable state initially rendered from it.
 *
 * @param sourceValue - Exact persisted value to preserve while its displayed state is unchanged.
 * @param editableValue - Editable axis value derived from the persisted value.
 * @param getEditableState - Creates the comparable representation of an editable axis.
 * @returns The source value and its initial comparable editable state.
 */
export function createCoordinateAxisSource<TSourceValue, TEditableValue, TEditableState>(
	sourceValue: TSourceValue,
	editableValue: TEditableValue,
	getEditableState: (value: TEditableValue) => TEditableState
): CoordinateAxisSource<TSourceValue, TEditableState> {
	return {
		value: sourceValue,
		editableState: getEditableState(editableValue)
	};
}

/**
 * Preserves the exact source value while an axis still matches its initial editable state.
 *
 * Once the displayed state changes, the supplied converter becomes the canonical value source.
 *
 * @param editableValue - Current editable axis value.
 * @param source - Persisted source value and its initial editable representation.
 * @param getEditableState - Creates the comparable representation of an editable axis.
 * @param convertValue - Converts changed editable input to its persisted representation.
 * @returns The exact source value when unchanged, otherwise the converted editable value.
 */
export function resolveCoordinateAxisValue<TSourceValue, TEditableValue, TEditableState, TConvertedValue>(
	editableValue: TEditableValue,
	source: CoordinateAxisSource<TSourceValue, TEditableState>,
	getEditableState: (value: TEditableValue) => TEditableState,
	convertValue: (value: TEditableValue) => TConvertedValue
): TSourceValue | TConvertedValue {
	if(hasCoordinatePart(source?.value) && getEditableState(editableValue) === source.editableState) {
		return source.value;
	}

	return convertValue(editableValue);
}
